import { AlertCircle, MessageCircle, ShieldCheck, Phone, RefreshCw, MapPin, Camera, FileDown, CheckCircle2, XCircle } from "lucide-react";
import { COMPANY, EXTRAS, FIXED_EXTRAS, PRICE_LEVELS, MATERIAL_TAGS } from "@/data/services";
import { formatEUR } from "@/lib/calc";

const VARIATION_FACTORS = [
  "Estado real del soporte",
  "Accesos a la vivienda o local",
  "Altura o dificultad del trabajo",
  "Retirada de escombros",
  "Necesidad de contenedor",
  "Calidad de materiales",
  "Urgencia",
  "Vivienda ocupada o amueblada",
  "Remates especiales",
  "Licencias o tasas si fueran necesarias",
];

export default function Step3({
  item,
  quantity,
  priceLevelId,
  selectedExtras,
  selectedFixed,
  estimate,
  notes,
  propertyType,
  timing,
  onRequest,
  onReset,
  onExportPDF,
  submitting,
  exporting,
}) {
  const level = PRICE_LEVELS.find(p => p.id === priceLevelId);
  const extrasList = EXTRAS.filter(e => selectedExtras.includes(e.id));
  const fixedList = FIXED_EXTRAS.filter(f => selectedFixed.includes(f.id));
  const matTag = item?.material ? MATERIAL_TAGS[item.material] : null;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="calc-step-3">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#004B87] font-semibold mb-2">Paso 3</div>
        <h2 className="font-display font-semibold text-2xl md:text-3xl text-slate-900 tracking-tight">
          Estimación orientativa para su reforma
        </h2>
        <p className="text-slate-500 mt-2 text-sm">Revisa los datos y solicita una visita técnica para confirmar el presupuesto.</p>
      </div>

      {/* Total destacado */}
      <div
        className="relative overflow-hidden rounded-2xl bg-[#004B87] text-white p-7 md:p-10 animate-pulse-glow"
        data-testid="total-estimated-container"
      >
        <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-white/5 blur-2xl" />
        <div className="relative">
          <div className="text-[11px] uppercase tracking-[0.25em] font-semibold text-blue-200">
            Precio estimado orientativo
          </div>
          <div className="flex items-baseline gap-3 mt-3 flex-wrap">
            <span className="text-blue-200 font-display text-2xl md:text-3xl">desde</span>
            <span
              data-testid="total-estimated-amount"
              className="font-display font-bold text-5xl md:text-7xl leading-none tracking-tighter"
            >
              {formatEUR(estimate.total)}
            </span>
          </div>
          <div className="text-blue-100/90 mt-3 text-sm">
            IVA no incluido · Pendiente de visita técnica y medición real
            {estimate.minApplied && <span className="ml-2 inline-block px-2 py-0.5 rounded bg-white/10 text-[11px]">Intervención mínima aplicada</span>}
          </div>
        </div>
      </div>

      {/* Detalle estructurado */}
      <div className="rounded-xl border border-slate-200 bg-white p-5 md:p-7" data-testid="breakdown">
        <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-4">Detalle de la estimación</div>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
          <Field label="Servicio seleccionado" value={item?.name} />
          <Field label="Unidad" value={item?.unit} />
          <Field label="Medición" value={`${quantity} ${item?.unit}`} />
          <Field label="Precio base" value={`${item?.price} €/${item?.unit}`} />
          <Field label="Subtotal" value={formatEUR((item?.price || 0) * Number(quantity || 0))} />
          <Field label="Nivel seleccionado" value={`${level?.label} (×${level?.factor.toFixed(2)})`} />
          <Field
            label="Suplementos aplicados"
            value={extrasList.length ? extrasList.map(e => `${e.label} +${Math.round(e.pct*100)}%`).join(', ') : 'Ninguno'}
          />
          <Field
            label="Conceptos adicionales"
            value={fixedList.length ? fixedList.map(f => `${f.label} (${f.amount}€)`).join(', ') : 'Ninguno'}
          />
          <Field label="IVA" value="No incluido" />
          {matTag && <Field label="Material" value={matTag.label} />}
          {propertyType && <Field label="Tipo de vivienda/local" value={propertyType} />}
          {timing && <Field label="Plazo deseado" value={timing} />}
          <Field label="Total estimado" value={formatEUR(estimate.total)} highlight fullWidth />
          {notes && <Field label="Observaciones" value={notes} fullWidth />}
        </dl>
      </div>

      {/* Incluye / No incluye */}
      {(item?.incluye?.length || item?.noIncluye?.length) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="incluye-block">
          {item?.incluye?.length > 0 && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-5">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-4 h-4 text-emerald-700" />
                <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">Incluye</div>
              </div>
              <ul className="space-y-1.5 text-sm text-emerald-900">
                {item.incluye.map((t, i) => <li key={i} className="flex gap-2"><span className="text-emerald-600">·</span><span>{t}</span></li>)}
              </ul>
            </div>
          )}
          {item?.noIncluye?.length > 0 && (
            <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-5">
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-4 h-4 text-amber-700" />
                <div className="text-[11px] uppercase tracking-[0.2em] text-amber-700 font-semibold">No incluye</div>
              </div>
              <ul className="space-y-1.5 text-sm text-amber-900">
                {item.noIncluye.map((t, i) => <li key={i} className="flex gap-2"><span className="text-amber-600">·</span><span>{t}</span></li>)}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Avisos importantes */}
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 md:p-6" data-testid="disclaimer">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs md:text-sm text-amber-900 leading-relaxed space-y-3 flex-1">
            <p className="font-semibold">Esta estimación es orientativa.</p>
            <p>El presupuesto definitivo se confirmará tras visita técnica, medición real y revisión del estado de la obra.</p>
            <p>Los precios mostrados <strong>no constituyen una oferta contractual cerrada</strong>. El presupuesto final será válido únicamente tras revisión técnica y aceptación por escrito de {COMPANY.name}.</p>
            <div>
              <p className="mb-2">El precio puede variar según:</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 list-disc pl-5">
                {VARIATION_FACTORS.map(f => <li key={f}>{f}</li>)}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Bloque visita técnica */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 md:p-7">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#E6F0FA] text-[#004B87] grid place-items-center shrink-0">
            <MapPin className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold text-slate-900 text-base md:text-lg">Solicita una visita técnica</div>
            <p className="text-slate-600 text-sm mt-2 leading-relaxed">
              Atendemos por WhatsApp, teléfono y visitas presenciales en obra en {COMPANY.area}.
              Nuestro equipo se desplaza para revisar medidas, estado del soporte, accesos, materiales y condiciones reales del trabajo.
            </p>
          </div>
        </div>
      </div>

      {/* Aviso fotos por WhatsApp */}
      <div className="rounded-xl border border-[#004B87]/20 bg-[#E6F0FA]/40 p-4 md:p-5 flex items-start gap-3">
        <Camera className="w-5 h-5 text-[#004B87] shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700 leading-relaxed">
          <strong>Puede enviarnos fotos del trabajo</strong> directamente por WhatsApp para valorar mejor la estimación antes de la visita técnica.
        </p>
      </div>

      {/* CTAs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={onRequest}
          disabled={submitting}
          data-testid="request-appointment-btn"
          className="inline-flex items-center justify-center gap-3 h-14 px-6 rounded-xl bg-[#004B87] hover:bg-[#003A6A] disabled:opacity-60 text-white font-display font-semibold text-base transition-colors shadow-lg shadow-[#004B87]/20"
        >
          <MessageCircle className="w-5 h-5" />
          {submitting ? "Abriendo WhatsApp…" : "Solicitar visita técnica por WhatsApp"}
        </button>
        <button
          type="button"
          onClick={onExportPDF}
          disabled={exporting}
          data-testid="export-pdf-btn"
          className="inline-flex items-center justify-center gap-3 h-14 px-6 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-60 text-white font-display font-semibold text-base transition-colors shadow-lg"
        >
          <FileDown className="w-5 h-5" />
          {exporting ? "Generando PDF…" : "Descargar estimación en PDF"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <a
          href={`tel:+34${COMPANY.whatsapp}`}
          data-testid="call-direct-btn"
          className="inline-flex items-center justify-center gap-3 h-12 px-6 rounded-xl border border-slate-300 bg-white hover:border-[#004B87] hover:text-[#004B87] text-slate-900 font-medium text-sm transition-colors"
        >
          <Phone className="w-4 h-4" />
          Llamar al {COMPANY.whatsappFormatted}
        </a>
        <button
          type="button"
          onClick={onReset}
          data-testid="new-estimation-btn"
          className="inline-flex items-center justify-center gap-3 h-12 px-6 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-[#004B87] text-slate-700 hover:text-[#004B87] font-medium text-sm transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Hacer nueva estimación
        </button>
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-500 pt-2">
        <ShieldCheck className="w-4 h-4 text-[#004B87]" />
        {COMPANY.name} · NIF {COMPANY.nif} · Personal asegurado · Garantía de obra
      </div>
    </div>
  );
}

function Field({ label, value, highlight, fullWidth }) {
  return (
    <div className={fullWidth ? "md:col-span-2" : ""}>
      <dt className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">{label}</dt>
      <dd className={"mt-1 " + (highlight ? "font-display font-bold text-lg text-[#004B87]" : "text-slate-900 text-sm")}>
        {value || "—"}
      </dd>
    </div>
  );
}
